import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Camera, Home, DollarSign, Clock, CheckCircle, ArrowRight, Plane, Box, Image as ImageIcon } from 'lucide-react';
import { useFooterGlow } from '../context/FooterGlowContext';
import './RealEstate.css';

const RealEstate = () => {
  const { triggerGlow } = useFooterGlow();

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="real-estate-page">
      {/* Hero Section */}
      <section className="hero-section" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/zac-gudakov-wwqZ8CM21gg-unsplash.jpg)`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}>
        <div className="container">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h1 
              className="hero-title"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Real Estate Photography
            </motion.h1>
            <motion.p 
              className="hero-subtitle"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Showcase properties in their best light with professional real estate photography. 
              From luxury homes to commercial spaces, we capture the essence that sells.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Service Details */}
      <section className="service-details">
        <div className="container">
          <motion.div 
            className="details-grid"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <motion.div className="detail-card" variants={fadeInUp}>
              <div className="detail-icon">
                <Camera size={40} />
              </div>
              <h3>Professional Equipment</h3>
              <p>High-end cameras and lenses ensure every shot captures the property's true beauty and potential.</p>
            </motion.div>

            <motion.div className="detail-card" variants={fadeInUp}>
              <div className="detail-icon">
                <Home size={40} />
              </div>
              <h3>Interior & Exterior</h3>
              <p>Comprehensive coverage including stunning exterior shots, interior spaces, and architectural details.</p>
            </motion.div>

            <motion.div className="detail-card" variants={fadeInUp}>
              <div className="detail-icon">
                <DollarSign size={40} />
              </div>
              <h3>Competitive Pricing</h3>
              <p>Professional quality at competitive rates. Packages designed for real estate agents and property owners.</p>
            </motion.div>

            <motion.div className="detail-card" variants={fadeInUp}>
              <div className="detail-icon">
                <Clock size={40} />
              </div>
              <h3>Quick Turnaround</h3>
              <p>Fast delivery times to meet your listing deadlines. 1-2 day delivery available for urgent projects.</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* What's Included */}
      <section className="included-section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>What's Included</h2>
            <p>Comprehensive real estate photography packages designed to maximize your property's appeal</p>
          </motion.div>

          <motion.div 
            className="included-grid"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <motion.div className="included-item" variants={fadeInUp}>
              <CheckCircle size={24} />
              <span>Professional HDR photography</span>
            </motion.div>
            <motion.div className="included-item" variants={fadeInUp}>
              <CheckCircle size={24} />
              <span>Interior and exterior shots</span>
            </motion.div>
            <motion.div className="included-item" variants={fadeInUp}>
              <CheckCircle size={24} />
              <span>Wide-angle lens coverage</span>
            </motion.div>
            <motion.div className="included-item" variants={fadeInUp}>
              <CheckCircle size={24} />
              <span>Professional editing and retouching</span>
            </motion.div>
            <motion.div className="included-item" variants={fadeInUp}>
              <CheckCircle size={24} />
              <span>High-resolution digital files</span>
            </motion.div>
            <motion.div className="included-item" variants={fadeInUp}>
              <CheckCircle size={24} />
              <span>Virtual tour preparation</span>
            </motion.div>
            <motion.div className="included-item" variants={fadeInUp}>
              <CheckCircle size={24} />
              <span>Social media optimized images</span>
            </motion.div>
            <motion.div className="included-item" variants={fadeInUp}>
              <CheckCircle size={24} />
              <span>1-2 day delivery available</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Portfolio Gallery */}
      <section className="portfolio-section section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Portfolio Gallery</h2>
            <p>Explore some of our professional real estate photography</p>
          </motion.div>

          <div className="portfolio-grid">
            <motion.div 
              className="portfolio-item"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <img 
                src={`${process.env.PUBLIC_URL}/francesca-tosolini-XAHSexPxSus-unsplash.jpg`}
                alt="Modern interior with wooden staircase"
                className="portfolio-image"
                loading="lazy"
              />
            </motion.div>
            <motion.div 
              className="portfolio-item"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <img 
                src={`${process.env.PUBLIC_URL}/jason-briscoe-UV81E0oXXWQ-unsplash.jpg`}
                alt="Spacious living and dining area"
                className="portfolio-image"
                loading="lazy"
              />
            </motion.div>
            <motion.div 
              className="portfolio-item"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <img 
                src={`${process.env.PUBLIC_URL}/steven-ungermann-g4zOyqK_QsY-unsplash.jpg`}
                alt="Modern kitchen and dining space"
                className="portfolio-image"
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="pricing-section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Pricing Packages</h2>
            <p>Professional real estate photography services tailored to your needs</p>
          </motion.div>

          {/* Base Package - Horizontal */}
          <motion.div 
            className="base-package-container"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.div className="pricing-card featured base-package" variants={fadeInUp}>
              <div className="base-package-content">
                <div className="base-package-left">
                  <div className="package-icon">
                    <Camera size={32} />
                  </div>
                  <div className="base-package-info">
                    <h3>Base Photography</h3>
                    <div className="price">$225</div>
                    <p className="price-note">For properties under 2,000 sqft</p>
                  </div>
                </div>
                <div className="base-package-right">
                  <ul className="package-features">
                    <li>10-25 professional photos</li>
                    <li>Interior & exterior coverage</li>
                    <li>Walkthrough / single location</li>
                    <li>Professional editing & HDR</li>
                    <li>High-resolution digital files</li>
                    <li className="price-additional">+ $50 per 1,000 sqft over 2,000</li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Add-ons Section */}
          <motion.div 
            className="addons-section"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h3 className="addons-title">Add-Ons</h3>
            <motion.div 
              className="addons-grid"
              variants={staggerContainer}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
            >
              <motion.div className="pricing-card addon-card" variants={fadeInUp}>
                <div className="package-header">
                  <div className="package-icon">
                    <Plane size={32} />
                  </div>
                  <h3>Aerial Photography</h3>
                  <div className="price">$75</div>
                </div>
                <ul className="package-features">
                  <li>Drone aerial shots</li>
                  <li>Stunning overhead views</li>
                  <li>Property context & surroundings</li>
                  <li>Professional editing</li>
                  <li>High-resolution files</li>
                </ul>
              </motion.div>

              <motion.div className="pricing-card addon-card" variants={fadeInUp}>
                <div className="package-header">
                  <div className="package-icon">
                    <Box size={32} />
                  </div>
                  <h3>3D Virtual Tour</h3>
                  <div className="price">$65</div>
                  <p className="price-note">For properties under 2,000 sqft</p>
                </div>
                <ul className="package-features">
                  <li>Interactive 3D walkthrough</li>
                  <li>360° panoramic views</li>
                  <li>Professional virtual tour</li>
                  <li>Easy sharing & embedding</li>
                  <li className="price-additional">+ $20 per 1,000 sqft over 2,000</li>
                </ul>
              </motion.div>

              <motion.div className="pricing-card addon-card" variants={fadeInUp}>
                <div className="package-header">
                  <div className="package-icon">
                    <ImageIcon size={32} />
                  </div>
                  <h3>Virtual Staging</h3>
                  <div className="price">$50</div>
                </div>
                <ul className="package-features">
                  <li>Photo virtual staging</li>
                  <li>Furniture & decor placement</li>
                  <li>Multiple style options</li>
                  <li>Realistic staging effects</li>
                  <li>Enhanced property appeal</li>
                </ul>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section section">
        <div className="container">
          <motion.div 
            className="cta-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Ready to Showcase Your Property?</h2>
            <p>Professional real estate photography that helps properties sell faster and for better prices.</p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default RealEstate; 